//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// data-sources imports
import { IContainer, IProps, IState } from "./DataSources.d";
import { dataSourcesFragments, query } from "./DataSources.gql";
import { styles } from "./DataSources.styles";
// other components
import { DataSource } from "@components/data-source";
import { DataSourceCreate } from "@components/data-source-create";
import { LogoutBtn } from "@components/logout-btn";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { RedButton } from "@clockwise/web-commons/src/ui/red-button";
// util
import { IReduxState } from "@reducers/root.reducer";
import { sortDataSources, ISortedDataSources } from "@util/data-source.util";
import { track, TrackingEvents } from "@util/analytics.util";
// constants
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { paths } from "@clockwise/client-commons/src/constants/site";
// material-ui and styles
import withStyles from "@material-ui/core/styles/withStyles";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { H1, P, A } from "@ui";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";

//////////////////
// COMPONENT
//////////////////
class DataSourcesCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      dialogOpen: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onDeleteDataSource = () => {
    this.props.relay.refetch({}, undefined, undefined, { force: true });
  };

  private onLearnMore = () => {
    track(TrackingEvents.HELP.PERSONAL_CALENDAR_SYNC);
  };

  private onDataSourceCreateError = (authError: ISchema.DataSourceAuthError) => {
    switch (authError) {
      case ISchema.DataSourceAuthError.SameDomainError:
        return this.props.snackBarOpen(
          "Personal calendars cannot be on the same domain as your work calendar.",
        );
      case ISchema.DataSourceAuthError.GenericError:
        return this.props.snackBarOpen(
          "There was a problem connecting this calendar as a personal calendar",
        );
      default:
        return "";
    }
  };

  private onDataSourceCreateSuccess = () => {
    this.props.snackBarOpen("Your personal calendar is now connected and syncing.");
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderWorkSources = (sortedDataSources: ISortedDataSources) => {
    if (!sortedDataSources.work.length) {
      return <span />;
    }
    return (
      <div>
        <div className={this.props.classes.work}>
          <H1 className={this.props.classes.h1} style={{ paddingTop: 24 }}>
            {/* {sortedDataSources.work[0].name} */}
            {this.props.org.primaryOrgCalendar}
          </H1>
          <div className={this.props.classes.links}>
            <A style={{ marginRight: 20 }} onClick={() => this.setState({ dialogOpen: true })}>
              Deactivate your Clockwise account
            </A>
            <LogoutBtn anchor onLogout={this.props.onLogout} />
          </div>
        </div>
        <Dialog open={this.state.dialogOpen}>
          <DialogTitle>
            <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
              Are you sure you want to deactivate your Clockwise account?
            </H1>
          </DialogTitle>
          <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
            <P
              style={{
                fontSize: 15,
                lineHeight: 1.8,
                color: greys.slate,
                marginBottom: 30,
              }}
            >
              This action cannot be undone. It may take up to a few hours to process your
              deactivation request.
            </P>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={() => this.setState({ dialogOpen: false })}>
              Cancel
            </Button>
            <RedButton
              onClick={() => {
                track(TrackingEvents.SETTINGS.DATA_SOURCE_DEACTIVATE_ACCOUNT);
                window.open(paths.uninstall, "_blank");
                this.setState({ dialogOpen: false });
              }}
              label="Deactivate"
            />
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  private renderPersonalSources = (sortedDataSources: ISortedDataSources) => {
    const { org, hideHeaderText } = this.props;
    return (
      <div>
        {!hideHeaderText ? (
          <>
            <H1 className={this.props.classes.h1} style={{ paddingTop: 24 }}>
              Personal calendar sync
            </H1>
            <div className={this.props.classes.intro}>
              Personal calendar sync automatically syncs events from your personal Google Calendar
              account to work calendar. Coworkers with access to your calendar will see an event
              with a title of "Busy (via Clockwise)."&nbsp;
              <A target="_blank" href={articleUrls.personalCalSync} onClick={this.onLearnMore}>
                Learn more.
              </A>
            </div>
          </>
        ) : (
          ""
        )}
        {sortedDataSources.other.map((ds) => {
          return (
            <DataSource key={ds.id} dataSource={ds} org={org} onDelete={this.onDeleteDataSource} />
          );
        })}
        <div style={{ paddingTop: "10px" }}>
          <DataSourceCreate
            org={org}
            onError={this.onDataSourceCreateError}
            onSuccess={this.onDataSourceCreateSuccess}
          />
        </div>
      </div>
    );
  };

  public render() {
    const { org, user, showType } = this.props;
    const sortedDataSources = sortDataSources(user.dataSources.edges, org.domains);

    return (
      <div className={this.props.classes.container}>
        {showType === "work" ? this.maybeRenderWorkSources(sortedDataSources) : null}
        {showType === "personal" ? this.renderPersonalSources(sortedDataSources) : null}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const DataSourcesStyled = withStyles(styles)(DataSourcesCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const DataSourcesRedux = connect(mapStateToProps, {
  snackBarOpen,
})(DataSourcesStyled);

//////////////////
// RELAY
//////////////////
export const DataSources = createRefetchContainer<IContainer>(
  DataSourcesRedux,
  dataSourcesFragments,
  query,
);
